import {
	faFileInvoiceDollar,
	faMagnifyingGlass,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isInvoiceCreditMemo } from '@supply-mover-2-0/shared';
import { DateRangePicker, useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { AccountLocationPicker } from '../components/AccountLocationPicker';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { Paginator } from '../components/Paginator';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { parseIsoDate, toIsoDate } from '../lib/dateRange';
import { formatCurrency } from '../lib/formatCurrency';
import * as portalInvoiceService from '../services/portalInvoiceService';
import { useLoginModalStore } from '../stores/loginModalStore';

const PAGE_SIZE = 20;

/**
 * Invoice history page with date range and payment status filters.
 * Shows invoices across all assigned clients.
 */
export function InvoicesPage(): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated, availableClients } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const searchQuery = searchParams.get('q') ?? '';
	const dateFrom = searchParams.get('dateFrom') ?? '';
	const dateTo = searchParams.get('dateTo') ?? '';
	const paymentStatus = searchParams.get('paymentStatus') ?? '';
	const clientIdParam = searchParams.get('clientId') ?? '';
	const typeParam = searchParams.get('type') ?? '';

	const [searchInput, setSearchInput] = useState(searchQuery);

	const { data, isLoading } = useQuery({
		queryKey: [
			'invoices',
			{
				page: currentPage,
				search: searchQuery,
				dateFrom,
				dateTo,
				paymentStatus,
				clientId: clientIdParam,
				type: typeParam,
			},
		],
		queryFn: () =>
			portalInvoiceService.getInvoices(token ?? '', {
				page: currentPage,
				limit: PAGE_SIZE,
				search: searchQuery.length > 0 ? searchQuery : undefined,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
				dateFrom: dateFrom.length > 0 ? dateFrom : undefined,
				dateTo: dateTo.length > 0 ? dateTo : undefined,
				paymentStatus:
					paymentStatus.length > 0
						? (paymentStatus as 'paid' | 'unpaid' | 'partial')
						: undefined,
				type:
					typeParam.length > 0
						? (typeParam as 'invoice' | 'creditMemo')
						: undefined,
			}),
		enabled: token != null,
		staleTime: 30_000,
	});

	const invoices = data?.data ?? [];
	const pagination = data?.pagination ?? null;

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handleSearchSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			updateParams({
				q: searchInput.trim().length > 0 ? searchInput.trim() : null,
				page: null,
			});
		},
		[searchInput, updateParams],
	);

	const handleClientChange = useCallback(
		(value: string) => {
			updateParams({ clientId: value.length > 0 ? value : null, page: null });
		},
		[updateParams],
	);

	const handleDateRangeChange = useCallback(
		(dates: [Date | null, Date | null]) => {
			updateParams({
				dateFrom: toIsoDate(dates[0]),
				dateTo: toIsoDate(dates[1]),
				page: null,
			});
		},
		[updateParams],
	);

	const handlePaymentStatusChange = useCallback(
		(status: string) => {
			updateParams({
				paymentStatus: status.length > 0 ? status : null,
				page: null,
			});
		},
		[updateParams],
	);

	const handleTypeChange = useCallback(
		(value: string) => {
			updateParams({ type: value.length > 0 ? value : null, page: null });
		},
		[updateParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateParams({ page: page > 1 ? String(page) : null });
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
		[updateParams],
	);

	const getPaymentStatusLabel = (invoice: {
		outstandingBalance: string | null;
		total: string | null;
	}): string => {
		if (
			invoice.outstandingBalance == null ||
			parseFloat(invoice.outstandingBalance) === 0
		) {
			return t('Paid');
		}
		if (invoice.total != null && invoice.outstandingBalance === invoice.total) {
			return t('Unpaid');
		}
		return t('Partial');
	};

	const getPaymentStatusColor = (invoice: {
		outstandingBalance: string | null;
		total: string | null;
	}): string => {
		if (
			invoice.outstandingBalance == null ||
			parseFloat(invoice.outstandingBalance) === 0
		) {
			return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
		}
		if (invoice.total != null && invoice.outstandingBalance === invoice.total) {
			return 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300';
		}
		return 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
	};

	// Prompt login if not authenticated
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Invoices" backPath="/account" />
			<AccountBreadcrumb current="Invoices" />
			<div className="flex items-center justify-between mb-4">
				<h1 className="hidden md:block text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
					{t('Invoices')}
				</h1>
				{/* PD-14057 — pay several invoices at once / add money on account */}
				<button
					type="button"
					onClick={() => {
						void navigate('/payments/make');
					}}
					className="ml-auto px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
				>
					{t('Make a Payment')}
				</button>
			</div>

			{/* Search bar */}
			<form onSubmit={handleSearchSubmit} className="relative mb-4">
				<FontAwesomeIcon
					icon={faMagnifyingGlass}
					className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
				/>
				<input
					type="search"
					value={searchInput}
					onChange={(e) => {
						setSearchInput(e.target.value);
					}}
					placeholder={t('Search by invoice # or item...')}
					className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-brand-surface border border-brand-border text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400"
				/>
			</form>

			{/* Filters — one row: the Date range selector, then the dropdown filters. */}
			<div className="mb-4 bg-brand-surface border border-brand-border rounded-xl p-4">
				<div className="flex flex-wrap items-end gap-3">
					{/* Date range */}
					<div className="min-w-[15rem]">
						<label className="block text-xs font-medium text-brand-muted mb-1">
							{t('Date')}
						</label>
						<DateRangePicker
							startDate={parseIsoDate(dateFrom)}
							endDate={parseIsoDate(dateTo)}
							onChange={handleDateRangeChange}
							placeholderText={t('Select date range...')}
						/>
					</div>
					{/* Client/location filter — grouped by relationship (PD-14189) */}
					{availableClients.length > 1 && (
						<div className="flex-1 min-w-[9rem]">
							<AccountLocationPicker
								value={clientIdParam}
								onChange={handleClientChange}
								clients={availableClients}
								className="w-full"
							/>
						</div>
					)}
					{/* Type filter — Invoice vs Credit Memo (PD-14172) */}
					<div className="flex-1 min-w-[9rem]">
						<label className="block text-xs font-medium text-brand-muted mb-1">
							{t('Type')}
						</label>
						<select
							value={typeParam}
							onChange={(e) => {
								handleTypeChange(e.target.value);
							}}
							className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm"
						>
							<option value="">{t('All')}</option>
							<option value="invoice">{t('Invoices')}</option>
							<option value="creditMemo">{t('Credit Memos')}</option>
						</select>
					</div>
					<div className="flex-1 min-w-[9rem]">
						<label className="block text-xs font-medium text-brand-muted mb-1">
							{t('Payment Status')}
						</label>
						<select
							value={paymentStatus}
							onChange={(e) => {
								handlePaymentStatusChange(e.target.value);
							}}
							className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm"
						>
							<option value="">{t('All')}</option>
							<option value="paid">{t('Paid')}</option>
							<option value="unpaid">{t('Unpaid')}</option>
							<option value="partial">{t('Partial')}</option>
						</select>
					</div>
				</div>
			</div>

			{/* Loading */}
			{isLoading && (
				<div className="flex items-center justify-center py-12">
					<FontAwesomeIcon
						icon={faSpinner}
						spin
						className="text-2xl text-gray-400"
					/>
				</div>
			)}

			{/* Invoice list */}
			{!isLoading && invoices.length > 0 && (
				<div className="space-y-2">
					{invoices.map((invoice) => {
						// A credit memo is `type='2'` or a negative-total row (some
						// ERP feeds emit credits as `type='1'` with a negative total).
						const isCreditMemo =
							isInvoiceCreditMemo(invoice.type) ||
							(invoice.total != null && parseFloat(invoice.total) < 0);
						return (
							<button
								key={invoice.id}
								type="button"
								onClick={() => {
									void navigate(`/invoices/${invoice.id}`);
								}}
								className="w-full text-left bg-brand-surface border border-brand-border rounded-xl p-4 hover:shadow-sm transition-shadow"
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<div className="flex items-center gap-2 mb-1">
											<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
												{isCreditMemo ? t('Credit Memo') : t('Invoice')} #
												{t(invoice.externalId ?? String(invoice.id))}
											</span>
											{/* Payment status is meaningless for a credit memo —
											    the "Credit Memo" label above already identifies it. */}
											{!isCreditMemo && (
												<span
													className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPaymentStatusColor(invoice)}`}
												>
													{getPaymentStatusLabel(invoice)}
												</span>
											)}
										</div>
										{invoice.clientName != null && (
											<p className="text-xs text-brand-muted">
												{t(invoice.clientName)}
											</p>
										)}
										<p className="text-xs text-brand-muted mt-0.5">
											{t('Date')}: {t(invoice.date ?? '')}
											{invoice.dueDate != null && (
												<>
													{' · '}
													{t('Due')}: {t(invoice.dueDate)}
												</>
											)}
										</p>
									</div>
									<div className="text-right shrink-0">
										<div className="text-sm font-bold text-gray-900 dark:text-gray-100">
											{t(formatCurrency(invoice.total) ?? '-')}
										</div>
										{invoice.outstandingBalance != null &&
											parseFloat(invoice.outstandingBalance) > 0 && (
												<div className="text-xs text-red-600 dark:text-red-400 mt-0.5">
													{t('Balance')}:{' '}
													{t(formatCurrency(invoice.outstandingBalance) ?? '')}
												</div>
											)}
										{invoice.numberOfLines != null && (
											<div className="text-xs text-gray-400 mt-0.5">
												{t(String(invoice.numberOfLines))} {t('items')}
											</div>
										)}
									</div>
								</div>
							</button>
						);
					})}
				</div>
			)}

			{/* Empty state */}
			{!isLoading && invoices.length === 0 && (
				<div className="text-center py-12">
					<FontAwesomeIcon
						icon={faFileInvoiceDollar}
						className="text-4xl text-gray-300 dark:text-gray-600 mb-4"
					/>
					<p className="text-sm text-brand-muted">{t('No invoices found')}</p>
				</div>
			)}

			{/* Pagination */}
			{pagination != null && (
				<Paginator
					currentPage={currentPage}
					totalPages={pagination.totalPages}
					onPageChange={handlePageChange}
				/>
			)}
		</div>
	);
}
